<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ExtensionSlot from './ExtensionSlot.vue';

const { accountId } = useAccount();
const route = useRoute();
const { t } = useI18n();
const dialog = ref(null);
const active = shallowRef(null);
const context = computed(() => ({
  accountId: accountId.value,
  routeName: route.name,
  routePath: route.path,
}));
const dialogContext = computed(() => ({
  ...active.value?.context,
  ...context.value,
}));

const onClose = () => {
  const previous = active.value;
  active.value = null;
  previous?.onClose?.();
};
const close = () => {
  if (dialog.value) dialog.value.close();
  else onClose();
};
const bridge = {
  openDialog(request) {
    if (Number(request.context.accountId) !== Number(accountId.value)) {
      throw new Error('Extension dialog account does not match dashboard');
    }
    if (active.value) throw new Error('An extension dialog is already open');
    const entry = {
      ...request,
      finish() {
        if (active.value !== entry) return;
        onClose();
      },
    };
    active.value = entry;
    nextTick(() => {
      if (active.value === entry) dialog.value?.open();
    });
    return () => {
      if (active.value === entry) close();
    };
  },
};

onMounted(() => {
  window.ChatwootExtensionUI = bridge;
});
watch([() => route.fullPath, accountId], close);
onBeforeUnmount(() => {
  onClose();
  if (window.ChatwootExtensionUI === bridge) {
    delete window.ChatwootExtensionUI;
  }
});
</script>

<template>
  <div class="flex-shrink-0 max-h-40 overflow-y-auto">
    <ExtensionSlot name="dashboard.header" :context="context" />
  </div>
  <Dialog
    v-if="active"
    ref="dialog"
    :title="active?.title || ''"
    :cancel-button-label="t('DIALOG.BUTTONS.CANCEL')"
    :show-confirm-button="false"
    overflow-y-auto
    @close="active.finish"
  >
    <ExtensionSlot
      v-if="active"
      :key="active.slot"
      :name="active.slot"
      :context="dialogContext"
    />
  </Dialog>
</template>
